import React, { useEffect, useRef, useCallback, useState, memo } from 'react';

/**
 * FluidBackground - Original design with performance optimizations
 * 
 * Performance features:
 * - Deferred initialization (loads after page is interactive)
 * - Mobile: CSS-only fallback (no canvas)
 * - Desktop: 30fps cap, throttled mouse events
 * - requestIdleCallback for non-blocking initialization
 */
const FluidBackground = memo(() => {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0, px: 0, py: 0 });
  const particlesRef = useRef([]);
  const animationRef = useRef(null);
  const [isMobile, setIsMobile] = useState(true); // Default mobile for SSR
  const [isReady, setIsReady] = useState(false);

  // Deferred initialization - don't block initial render
  useEffect(() => {
    const mobile = window.innerWidth < 768;
    setIsMobile(mobile);

    // On mobile, just show CSS background
    if (mobile) {
      setIsReady(true);
      return;
    }

    // Desktop: defer canvas initialization
    const initCanvas = () => setIsReady(true);
    
    if ('requestIdleCallback' in window) {
      requestIdleCallback(initCanvas, { timeout: 1500 });
    } else {
      setTimeout(initCanvas, 500);
    }
  }, []);

  const initParticles = useCallback((width, height, mobile) => {
    const particles = [];
    const numParticles = mobile ? 30 : 100;
    
    for (let i = 0; i < numParticles; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: 0,
        vy: 0,
        radius: Math.random() * 2 + 1,
        color: `hsla(${270 + Math.random() * 60}, 80%, ${50 + Math.random() * 20}%, ${0.4 + Math.random() * 0.3})`,
        baseX: Math.random() * width,
        baseY: Math.random() * height,
      });
    }
    return particles;
  }, []);

  useEffect(() => {
    // Skip canvas on mobile or until ready
    if (isMobile || !isReady) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let width = window.innerWidth;
    let height = window.innerHeight;

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
      particlesRef.current = initParticles(width, height, false);
    };

    resize();
    
    // Debounced resize
    let resizeTimeout;
    const debouncedResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(resize, 200);
    };
    window.addEventListener('resize', debouncedResize);

    // Throttled mouse tracking
    let lastMouseUpdate = 0;
    const handleMouseMove = (e) => {
      const now = Date.now();
      if (now - lastMouseUpdate < 32) return; // ~30fps
      lastMouseUpdate = now;
      
      mouseRef.current.px = mouseRef.current.x;
      mouseRef.current.py = mouseRef.current.y;
      mouseRef.current.x = e.clientX;
      mouseRef.current.y = e.clientY;
    };
    window.addEventListener('mousemove', handleMouseMove, { passive: true });

    let frameCount = 0;
    let lastFrameTime = 0;
    const frameInterval = 1000 / 30; // 30fps cap

    const animate = (timestamp) => {
      // FPS limiting
      const elapsed = timestamp - lastFrameTime;
      if (elapsed < frameInterval) {
        animationRef.current = requestAnimationFrame(animate);
        return;
      }
      lastFrameTime = timestamp - (elapsed % frameInterval);
      frameCount++;

      ctx.fillStyle = 'rgba(3, 0, 20, 0.15)';
      ctx.fillRect(0, 0, width, height);

      const mouse = mouseRef.current;
      const dx = mouse.x - mouse.px;
      const dy = mouse.y - mouse.py;
      const mouseSpeed = Math.sqrt(dx * dx + dy * dy);

      // Draw fluid trails
      if (mouseSpeed > 2) {
        const gradient = ctx.createRadialGradient(
          mouse.x, mouse.y, 0,
          mouse.x, mouse.y, 80 + mouseSpeed * 1.5
        );
        gradient.addColorStop(0, 'rgba(138, 43, 226, 0.25)');
        gradient.addColorStop(0.5, 'rgba(255, 0, 128, 0.08)');
        gradient.addColorStop(1, 'transparent');
        
        ctx.beginPath();
        ctx.arc(mouse.x, mouse.y, 80 + mouseSpeed * 1.5, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();
      }

      // Update and draw particles
      particlesRef.current.forEach((particle, i) => {
        const distX = mouse.x - particle.x;
        const distY = mouse.y - particle.y;
        const dist = Math.sqrt(distX * distX + distY * distY);

        if (dist < 150) {
          const angle = Math.atan2(distY, distX);
          const force = (150 - dist) / 150;
          particle.vx += Math.cos(angle + Math.PI / 2) * force * 0.3;
          particle.vy += Math.sin(angle + Math.PI / 2) * force * 0.3;
          particle.vx -= Math.cos(angle) * force * 0.15;
          particle.vy -= Math.sin(angle) * force * 0.15;
        }

        // Return to base
        particle.vx += (particle.baseX - particle.x) * 0.002;
        particle.vy += (particle.baseY - particle.y) * 0.002;

        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.vx *= 0.95;
        particle.vy *= 0.95;

        // Floating
        particle.y += Math.sin(timestamp * 0.0008 + i) * 0.2;

        // Draw with gradient
        const particleGradient = ctx.createRadialGradient(
          particle.x, particle.y, 0,
          particle.x, particle.y, particle.radius * 2.5
        );
        particleGradient.addColorStop(0, particle.color);
        particleGradient.addColorStop(1, 'transparent');
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius * 2.5, 0, Math.PI * 2);
        ctx.fillStyle = particleGradient;
        ctx.fill();

        // Connection lines
        if (i < 50) {
          for (let j = i + 1; j < Math.min(i + 10, particlesRef.current.length); j++) {
            const other = particlesRef.current[j];
            const dx = particle.x - other.x;
            const dy = particle.y - other.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance < 80) {
              ctx.beginPath();
              ctx.moveTo(particle.x, particle.y);
              ctx.lineTo(other.x, other.y);
              ctx.strokeStyle = `rgba(138, 43, 226, ${0.08 * (1 - distance / 80)})`;
              ctx.lineWidth = 0.5;
              ctx.stroke();
            }
          }
        }
      });

      // Ambient orbs
      const time = timestamp * 0.001;
      
      // Purple orb
      const orb1X = width * 0.3 + Math.sin(time * 0.3) * 80;
      const orb1Y = height * 0.4 + Math.cos(time * 0.2) * 60;
      const gradient1 = ctx.createRadialGradient(orb1X, orb1Y, 0, orb1X, orb1Y, 250);
      gradient1.addColorStop(0, 'rgba(138, 43, 226, 0.12)');
      gradient1.addColorStop(0.5, 'rgba(138, 43, 226, 0.04)');
      gradient1.addColorStop(1, 'transparent');
      ctx.beginPath();
      ctx.arc(orb1X, orb1Y, 250, 0, Math.PI * 2);
      ctx.fillStyle = gradient1;
      ctx.fill();

      // Pink orb
      const orb2X = width * 0.7 + Math.cos(time * 0.25) * 100;
      const orb2Y = height * 0.6 + Math.sin(time * 0.35) * 50;
      const gradient2 = ctx.createRadialGradient(orb2X, orb2Y, 0, orb2X, orb2Y, 200);
      gradient2.addColorStop(0, 'rgba(255, 0, 128, 0.08)');
      gradient2.addColorStop(0.5, 'rgba(255, 0, 128, 0.02)');
      gradient2.addColorStop(1, 'transparent');
      ctx.beginPath();
      ctx.arc(orb2X, orb2Y, 200, 0, Math.PI * 2);
      ctx.fillStyle = gradient2;
      ctx.fill();

      // Cyan orb
      const orb3X = width * 0.5 + Math.sin(time * 0.4) * 60;
      const orb3Y = height * 0.3 + Math.cos(time * 0.3) * 80;
      const gradient3 = ctx.createRadialGradient(orb3X, orb3Y, 0, orb3X, orb3Y, 150);
      gradient3.addColorStop(0, 'rgba(0, 240, 255, 0.06)');
      gradient3.addColorStop(0.5, 'rgba(0, 240, 255, 0.02)');
      gradient3.addColorStop(1, 'transparent');
      ctx.beginPath();
      ctx.arc(orb3X, orb3Y, 150, 0, Math.PI * 2);
      ctx.fillStyle = gradient3;
      ctx.fill();

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('resize', debouncedResize);
      window.removeEventListener('mousemove', handleMouseMove);
      clearTimeout(resizeTimeout);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [initParticles, isMobile, isReady]);

  // Mobile: CSS-only background
  if (isMobile) {
    return (
      <div 
        className="fixed inset-0 z-0 pointer-events-none"
        style={{ background: 'linear-gradient(180deg, #030014 0%, #0F0518 100%)' }}
      >
        <div 
          className="absolute rounded-full"
          style={{
            width: '300px',
            height: '300px',
            background: 'radial-gradient(circle, rgba(138,43,226,0.2) 0%, transparent 70%)',
            top: '20%',
            left: '15%',
            filter: 'blur(60px)',
          }}
        />
        <div 
          className="absolute rounded-full"
          style={{
            width: '250px',
            height: '250px',
            background: 'radial-gradient(circle, rgba(255,0,128,0.12) 0%, transparent 70%)',
            bottom: '25%',
            right: '15%',
            filter: 'blur(60px)',
          }}
        />
      </div>
    );
  }

  // Desktop: Canvas
  return (
    <>
      {/* Instant CSS background while canvas loads */}
      {!isReady && (
        <div 
          className="fixed inset-0 z-0 pointer-events-none"
          style={{ background: 'linear-gradient(180deg, #030014 0%, #0F0518 100%)' }}
        />
      )}
      {isReady && (
        <canvas
          ref={canvasRef}
          className="fixed inset-0 z-0 pointer-events-none"
          style={{ background: 'linear-gradient(180deg, #030014 0%, #0F0518 100%)' }}
        />
      )}
    </>
  );
});

FluidBackground.displayName = 'FluidBackground';

export default FluidBackground;
